import {
  PlayCircleIcon,
  PencilSquareIcon,
  DocumentPlusIcon,
  VideoCameraIcon,
  CurrencyDollarIcon,
  SpeakerWaveIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import {
  PlayCircleIcon as SolidPlayCircleIcon,
  PencilSquareIcon as SolidPencilSquareIcon,
  DocumentPlusIcon as SolidDocumentPlusIcon,
  VideoCameraIcon as SolidVideoCameraIcon,
  CurrencyDollarIcon as SolidCurrencyDollarIcon,
  SpeakerWaveIcon as SolidSpeakerWaveIcon,
  Bars3Icon as SolidBars3Icon,
} from '@heroicons/react/24/solid';
import React, { useContext, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Outlet, useParams } from 'react-router-dom';

import CollapsibleSidebar from 'components/CollapsibleSidebar/CollapsibleSidebar';
import SidebarAssessment from 'components/CollapsibleSidebar/SidebarAssessment';
import SidebarNavigationLink from 'components/CollapsibleSidebar/SidebarNavigationLink';
import ContentContainer from 'components/ContentContainer/ContentContainer';
import useGetAssessmentSteps from 'hooks/useGetAssessmentSteps';
import { t } from 'locales/i18n';
import { AssessmentDetailsContext } from 'providers/AssessmentDetailsProvider';

const Steps = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { isPractice } = useContext(AssessmentDetailsContext);

  const { interviewUid } = useParams();
  const { data: assessmentSteps, isLoading } = useGetAssessmentSteps(
    interviewUid ?? ''
  );

  return (
    <>
      <CollapsibleSidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      >
        <SidebarNavigationLink
          description={t('All Steps')}
          to="all-steps"
          icon={<Bars3Icon />}
          activeIcon={<SolidBars3Icon />}
          isCollapsed={!isSidebarOpen}
        />
        {isSidebarOpen && (
          <div className="m-2 w-full border-b border-gray-200" />
        )}
        {!isLoading ? (
          <>
            {!isPractice && (
              <>
                <SidebarNavigationLink
                  description={t('Welcome Video')}
                  dataTestId="welcomeVideo_sidebar"
                  to="welcome-video"
                  icon={<PlayCircleIcon />}
                  activeIcon={<SolidPlayCircleIcon />}
                  isCollapsed={!isSidebarOpen}
                  isShowing={assessmentSteps?.steps.welcome_video.active}
                />
                <SidebarNavigationLink
                  description={t('Registration')}
                  to="registration"
                  icon={<PencilSquareIcon />}
                  activeIcon={<SolidPencilSquareIcon />}
                  isCollapsed={!isSidebarOpen}
                  dataTestId="registration_sidebar"
                />
                <SidebarNavigationLink
                  description={t('Supplemental Materials')}
                  to="supplemental-materials"
                  icon={<DocumentPlusIcon />}
                  activeIcon={<SolidDocumentPlusIcon />}
                  isCollapsed={!isSidebarOpen}
                  dataTestId="supplementalMaterials_sidebar"
                  isShowing={
                    assessmentSteps?.steps.supplemental_materials.active
                  }
                />
                <SidebarNavigationLink
                  description={t('Device Setup')}
                  to="device-setup"
                  icon={<SpeakerWaveIcon />}
                  activeIcon={<SolidSpeakerWaveIcon />}
                  isCollapsed={!isSidebarOpen}
                  isShowing={assessmentSteps?.steps.device_setup.active}
                />
                <SidebarNavigationLink
                  description={t('Practice')}
                  to="practice"
                  icon={<VideoCameraIcon />}
                  activeIcon={<SolidVideoCameraIcon />}
                  isCollapsed={!isSidebarOpen}
                  isShowing={assessmentSteps?.steps.practice.active}
                />
                <SidebarNavigationLink
                  description={t('Payment')}
                  to="payment"
                  icon={<CurrencyDollarIcon />}
                  activeIcon={<SolidCurrencyDollarIcon />}
                  isCollapsed={!isSidebarOpen}
                  isShowing={assessmentSteps?.steps.payment.active}
                />
              </>
            )}
            <SidebarAssessment
              description={isPractice ? t('Practice') : t('Assessment')}
              activePath="competency"
              isCollapsed={!isSidebarOpen}
              isShowing={assessmentSteps?.steps.assessment.active}
            >
              {assessmentSteps?.steps.assessment.competencies.map(
                ({ name, uid }) => (
                  <SidebarNavigationLink
                    key={uid}
                    description={name}
                    to={`competency/${uid}`}
                    isCollapsed={!isSidebarOpen}
                    wrapperClassNames="py-2 prose-body-md-reg font-normal border-kira-slate-200 max-w-[17.5rem]"
                  />
                )
              )}
            </SidebarAssessment>
            {!isPractice && (
              <SidebarNavigationLink
                description={t('Closing Video')}
                dataTestId="closingVideo_sidebar"
                to="closing-video"
                icon={<PlayCircleIcon />}
                activeIcon={<SolidPlayCircleIcon />}
                isCollapsed={!isSidebarOpen}
                isShowing={assessmentSteps?.steps.closing_video.active}
              />
            )}
          </>
        ) : (
          <div className="grid px-3">
            <Skeleton height={50} />
          </div>
        )}
      </CollapsibleSidebar>
      <ContentContainer>
        <Outlet />
      </ContentContainer>
    </>
  );
};
export default Steps;
